'use client';

import type { Voice } from 'ml-text-service-client';
import { DEFAULT_MODEL_ID, VoiceoverModelId } from 'ml-text-service-client';
import { usePathname } from 'next/navigation';
import React, {
    createContext,
    type JSX,
    type PropsWithChildren,
    useCallback,
    useContext,
    useMemo,
    useReducer,
} from 'react';

import { AiContentVoiceoverType } from '@videoblocks/events-ts/lib/storyblocks/content/ai/AiContentVoiceoverTypeEnum';

/**
 * Here we track state storage for each individual tab. When switching tabs (/ urls), the UI
 * components are unmounted and re-mounted, causing internal state to be lost. This context
 * preserves any state needed for re-rendering (i.e. text prompts, generated audio URLs).
 */
export enum Tabs {
    TextToSpeech = 'textToSpeech',
    CloneMyVoice = 'cloneMyVoice',
    VoiceChanger = 'voiceChanger',
}

/**
 * map tab enum values to their corresponding AI content voiceover event types
 */
export const TAB_TO_EVENT_TYPE: Record<Tabs, AiContentVoiceoverType> = {
    [Tabs.TextToSpeech]: AiContentVoiceoverType.TEXT_TO_SPEECH,
    [Tabs.CloneMyVoice]: AiContentVoiceoverType.VOICE_CLONING,
    [Tabs.VoiceChanger]: AiContentVoiceoverType.VOICE_CHANGING,
};

/** Derive the active tab from the next/navigation pathname */
const SLUG_TO_TAB: Record<string, Tabs> = {
    'text-to-speech': Tabs.TextToSpeech,
    'clone-my-voice': Tabs.CloneMyVoice,
    'voice-changer': Tabs.VoiceChanger,
};

function deriveActiveTab(pathname: string): Tabs {
    const slug = pathname.split('/').pop() ?? '';
    return SLUG_TO_TAB[slug] ?? Tabs.TextToSpeech;
}

// state types per tab
interface TabState {
    generatedAudioUrl: string | null;
    generatedAudioHasPlayed: boolean;
    generatedAudioHasDownloaded: boolean;
    // per-tab event tracking ids
    aiContentId: string | null;
    lastAiContentId: string | null;
}

export interface TextToSpeechState extends TabState {
    generatedAudioVoiceId: Voice['voice_id'] | null;
    generatedAudioIsComplete: boolean;
    generatedAudioIsFetching: boolean;
    script: string | null;
    calculatedCost: number;
    selectedVoice: Voice['voice_id'];
    modelId: VoiceoverModelId;
    voiceoverParams: Record<string, string | number>;
}

export interface CloneMyVoiceState extends TabState {
    isUpload: boolean;
    generatedVoiceId: Voice['voice_id'] | null;
    generatedAudioIsComplete: boolean;
    generatedAudioIsFetching: boolean;
    script: string | null;
    calculatedCost: number;
    userAudioPreviewUrl: string | null;
    userAudioHasPlayed: boolean;
    voiceoverParams: Record<string, string | number>;
}

export interface VoiceChangerState extends TabState {
    uploadedAudioUrl: string | null;
    uploadedAudioHasPlayed: boolean;
    generatedAudioIsComplete: boolean;
    generatedAudioIsFetching: boolean;
    selectedVoice: Voice['voice_id'];
    userFile: File | null;
}

// map each Tab to its corresponding state
type TabStateMap = {
    [Tabs.TextToSpeech]: TextToSpeechState;
    [Tabs.CloneMyVoice]: CloneMyVoiceState;
    [Tabs.VoiceChanger]: VoiceChangerState;
};

// make updateState generic on the tab
export type VoiceoverTabContextType = {
    textToSpeech: TextToSpeechState;
    cloneMyVoice: CloneMyVoiceState;
    voiceChanger: VoiceChangerState;
    updateState: <T extends Tabs>(newState: Partial<TabStateMap[T]>) => void;
    activeTabState: TextToSpeechState | CloneMyVoiceState | VoiceChangerState;
    activeTab: Tabs;
    /**
     * Tracks the last voice that started preview playback from a VoiceCard.
     * Used to coordinate pausing between voice cards (when one starts, others should pause).
     * Note: This is NOT cleared on pause/end—it represents "last started", not "currently playing".
     */
    playingVoiceId: Voice['voice_id'] | null;
    setPlayingVoiceId: (voiceId: Voice['voice_id'] | null) => void;
};

// initial states per tab
export const initialTextToSpeech: TextToSpeechState = {
    generatedAudioUrl: null,
    generatedAudioHasPlayed: false,
    generatedAudioHasDownloaded: false,
    generatedAudioVoiceId: null, // voice id of generated audio
    generatedAudioIsComplete: true,
    generatedAudioIsFetching: false,
    script: null,
    calculatedCost: 0,
    selectedVoice: '1Do9vgAKVIXwweL5TATd', // first voice in list (Beth Yap who is a contributor voice)
    modelId: DEFAULT_MODEL_ID,
    voiceoverParams: { speed: 100 },
    aiContentId: null,
    lastAiContentId: null,
};

export const initialCloneMyVoice: CloneMyVoiceState = {
    isUpload: true,
    generatedVoiceId: null,
    generatedAudioIsComplete: true,
    generatedAudioIsFetching: false,
    script: null,
    calculatedCost: 0,
    userAudioPreviewUrl: null,
    generatedAudioUrl: null,
    userAudioHasPlayed: false,
    generatedAudioHasPlayed: false,
    generatedAudioHasDownloaded: false,
    voiceoverParams: { speed: 100 },
    aiContentId: null,
    lastAiContentId: null,
};

export const initialVoiceChanger: VoiceChangerState = {
    uploadedAudioUrl: null,
    generatedAudioUrl: null,
    uploadedAudioHasPlayed: false,
    generatedAudioHasPlayed: false,
    generatedAudioHasDownloaded: false,
    generatedAudioIsComplete: true,
    generatedAudioIsFetching: false,
    selectedVoice: '1Do9vgAKVIXwweL5TATd',
    userFile: null,
    aiContentId: null,
    lastAiContentId: null,
};

type VoiceoverTabState = {
    textToSpeech: TextToSpeechState;
    cloneMyVoice: CloneMyVoiceState;
    voiceChanger: VoiceChangerState;
    playingVoiceId: Voice['voice_id'] | null;
};

const initialState: VoiceoverTabState = {
    textToSpeech: initialTextToSpeech,
    cloneMyVoice: initialCloneMyVoice,
    voiceChanger: initialVoiceChanger,
    playingVoiceId: null,
};

type VoiceoverTabAction =
    | { type: 'SET_PLAYING_VOICE_ID'; voiceId: Voice['voice_id'] | null }
    | { type: 'UPDATE_TAB'; tab: Tabs; payload: Record<string, unknown> };

function voiceoverTabReducer(state: VoiceoverTabState, action: VoiceoverTabAction): VoiceoverTabState {
    switch (action.type) {
        case 'SET_PLAYING_VOICE_ID':
            return { ...state, playingVoiceId: action.voiceId };

        case 'UPDATE_TAB':
            switch (action.tab) {
                case Tabs.TextToSpeech:
                    return { ...state, textToSpeech: { ...state.textToSpeech, ...action.payload } };
                case Tabs.CloneMyVoice:
                    return { ...state, cloneMyVoice: { ...state.cloneMyVoice, ...action.payload } };
                case Tabs.VoiceChanger:
                    return { ...state, voiceChanger: { ...state.voiceChanger, ...action.payload } };
                default:
                    return state;
            }

        default:
            return state;
    }
}

export const VoiceoverTabContext = createContext<VoiceoverTabContextType>({
    textToSpeech: initialTextToSpeech,
    cloneMyVoice: initialCloneMyVoice,
    voiceChanger: initialVoiceChanger,
    activeTabState: {} as TextToSpeechState | CloneMyVoiceState | VoiceChangerState,
    activeTab: Tabs.TextToSpeech,
    updateState: () => {},
    playingVoiceId: null,
    setPlayingVoiceId: () => {},
});

export function VoiceoverTabProvider({ children }: PropsWithChildren): JSX.Element {
    const [state, dispatch] = useReducer(voiceoverTabReducer, initialState);

    const pathname = usePathname();
    const activeTab = deriveActiveTab(pathname);

    const setPlayingVoiceId = useCallback((voiceId: Voice['voice_id'] | null): void => {
        dispatch({ type: 'SET_PLAYING_VOICE_ID', voiceId });
    }, []);

    const updateState = useCallback(
        (newState: Record<string, unknown>): void => {
            dispatch({ type: 'UPDATE_TAB', tab: activeTab, payload: newState });
        },
        [activeTab],
    );

    const contextValue = useMemo<VoiceoverTabContextType>(() => {
        let activeTabState: TextToSpeechState | CloneMyVoiceState | VoiceChangerState;
        switch (activeTab) {
            case Tabs.TextToSpeech:
                activeTabState = state.textToSpeech;
                break;
            case Tabs.CloneMyVoice:
                activeTabState = state.cloneMyVoice;
                break;
            case Tabs.VoiceChanger:
                activeTabState = state.voiceChanger;
                break;
            default:
                activeTabState = state.textToSpeech;
        }

        return {
            activeTab,
            activeTabState,
            updateState,
            textToSpeech: state.textToSpeech,
            cloneMyVoice: state.cloneMyVoice,
            voiceChanger: state.voiceChanger,
            playingVoiceId: state.playingVoiceId,
            setPlayingVoiceId,
        };
    }, [state, activeTab, setPlayingVoiceId, updateState]);

    return <VoiceoverTabContext.Provider value={contextValue}>{children}</VoiceoverTabContext.Provider>;
}

export function useVoiceoverTabContext(): VoiceoverTabContextType {
    const ctx = useContext(VoiceoverTabContext);
    if (!ctx) throw new Error('useVoiceoverTabContext must be used within VoiceoverTabProvider');
    return ctx;
}
